import { useState } from "react";
import { toast } from "sonner";
import { useScanner } from "@/context/ScannerContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Bluetooth,
  Power,
  PowerOff,
  HelpCircle,
  ScanLine,
  CheckCircle2,
} from "lucide-react";

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export default function ScannerCard() {
  const { enabled, lastScans, bleStatus, enable, disable, tryBle } = useScanner();
  const [help, setHelp] = useState(false);

  const onTryBle = async () => {
    const res = await tryBle();
    if (res.ok) toast.success(`Connected to ${res.name}`);
    else toast.error(res.reason || "Could not connect");
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="font-display font-semibold flex items-center gap-2">
          <ScanLine className="h-5 w-5 text-primary" /> Scanner
        </div>
        <button
          type="button"
          onClick={() => setHelp(true)}
          className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
          data-testid="scanner-help"
        >
          <HelpCircle className="h-4 w-4" /> Pairing help
        </button>
      </div>
      <div className="panel-body space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className={`h-2.5 w-2.5 rounded-full ${
                enabled ? "bg-emerald-500 animate-pulse" : "bg-stone-300"
              }`}
            />
            <div>
              <div className="font-semibold" data-testid="scanner-status">
                {enabled ? "Connected · listening for scans" : "Not connected"}
              </div>
              <div className="text-xs text-stone-500">
                {enabled
                  ? "Scan anywhere outside a text field. Scans show below in real time."
                  : "Turn on the scanner listener after pairing your device."}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {enabled ? (
              <Button
                variant="outline"
                onClick={() => {
                  disable();
                  toast.success("Scanner listener stopped");
                }}
                data-testid="scanner-disconnect"
                className="h-11 gap-2"
              >
                <PowerOff className="h-4 w-4" /> Stop
              </Button>
            ) : (
              <Button
                onClick={() => {
                  enable();
                  toast.success("Scanner listener active");
                }}
                data-testid="scanner-connect"
                className="h-11 gap-2"
              >
                <Power className="h-4 w-4" /> Connect scanner
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onTryBle}
              data-testid="scanner-try-ble"
              className="h-11 gap-2"
              disabled={bleStatus === "connecting"}
              title="Try Web Bluetooth pairing (works on Chrome/Edge with BLE)"
            >
              <Bluetooth className="h-4 w-4" />
              {bleStatus === "connecting" ? "Pairing…" : "Bluetooth"}
            </Button>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-2">
            Recent scans
          </div>
          {lastScans.length === 0 ? (
            <div className="text-sm text-stone-500 py-3">
              Nothing scanned yet. Once the listener is on, every barcode will appear here.
            </div>
          ) : (
            <div
              className="space-y-1.5 max-h-44 overflow-y-auto pr-1"
              data-testid="scanner-recent"
            >
              {lastScans.map((s) => (
                <div
                  key={`${s.code}-${s.ts}`}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <div className="font-mono flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    {s.code}
                  </div>
                  <div className="text-xs text-stone-500 tnum">{timeAgo(s.ts)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-lg" data-testid="scanner-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Bluetooth className="h-5 w-5 text-primary" /> Pair your hand-held scanner
            </DialogTitle>
          </DialogHeader>
          <ol className="text-sm space-y-3 text-stone-700 list-decimal pl-5">
            <li>
              Power the scanner on and put it in <strong>HID / Bluetooth keyboard</strong> mode
              (most Zebra / Honeywell / Datalogic models — scan the "HID" or
              "Bluetooth keyboard" config barcode in the scanner manual).
            </li>
            <li>
              On this tablet or phone open <strong>Settings → Bluetooth</strong> and pair the
              scanner. It will appear as a keyboard.
            </li>
            <li>
              Come back to this dashboard and hit <strong>"Connect scanner"</strong>. The
              status light should turn green and any scan will appear in the
              "Recent scans" list within a second.
            </li>
            <li>
              From then on, scanning anywhere in the app — Goods Intake, Production →
              Consume Ingredients, Trace & Recall — works automatically.
            </li>
          </ol>
          <div className="text-xs text-stone-500 border-t border-border pt-3">
            <strong>Bluetooth button</strong> uses your browser's Web Bluetooth dialog to
            confirm pairing for BLE-mode scanners (Chrome / Edge on Android &amp; desktop).
            Most scanners still need OS-level pairing — this is just a quick verification.
          </div>
          <DialogFooter>
            <Button onClick={() => setHelp(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
